import type { z } from 'zod';

function getCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];
}

export class ApiError extends Error {
  status: number;
  code: string;
  threadId?: string;
  body: unknown;
  constructor(status: number, code: string, message: string, threadId?: string, body?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.threadId = threadId;
    this.body = body;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  idempotencyKey?: string;
}

async function request<T>(path: string, opts: RequestOptions, schema?: z.ZodType<T>): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const csrf = getCookie('usd_csrf');
  if (csrf && opts.method && opts.method !== 'GET') headers['x-csrf-token'] = csrf;
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    credentials: 'same-origin',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401) {
    if (window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    throw new ApiError(401, 'ISE-401', 'Not authenticated');
  }
  const text = await res.text();
  const parsed = text ? safeJson(text) : undefined;
  if (!res.ok) {
    const err = parsed as { code?: string; message?: string; threadId?: string } | undefined;
    throw new ApiError(
      res.status,
      err?.code ?? `HTTP-${res.status}`,
      err?.message ?? res.statusText,
      err?.threadId,
      parsed,
    );
  }
  if (schema) return schema.parse(parsed);
  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get<T>(path: string, schema?: z.ZodType<T>) {
    return request<T>(path, {}, schema);
  },
  post<T>(path: string, body?: unknown, schema?: z.ZodType<T>, idempotencyKey?: string) {
    return request<T>(path, { method: 'POST', body, idempotencyKey }, schema);
  },
  patch<T>(path: string, body?: unknown, schema?: z.ZodType<T>) {
    return request<T>(path, { method: 'PATCH', body }, schema);
  },
  delete<T>(path: string, schema?: z.ZodType<T>) {
    return request<T>(path, { method: 'DELETE' }, schema);
  },
};
